import { DirectoryItem, IDirectoryNavigationService, MetaRef, ItemType } from 'sharebase/services/directoryNavigationService'
import { 
    pxItemTypeGuards,
    PermissionType,
    ShareBaseFolder
} from 'phoenix/domain';
import { 
    IFolderService,
    ToastService
 } from 'sharebase/services';
import { ProgressService } from 'phoenix/services';

import { ShareBaseDialog } from 'sharebase/domain/sharebase-dialog';

require('./moveItemDialog.styles');

export const template = require('./moveItemDialog.template');


/**
 * Controller for the move item dialog.
 */
export class MoveItemDialogController extends ShareBaseDialog {
    public static $inject: string[] = [
        '$timeout',
        '$mdDialog',
        '$mdMedia',
        'pxProgressService',
        'SB_CHAR_LIMIT',
        'sbDirectoryNavigationService',
        'sbFolderService',
        'sbToastService',
        'gettextCatalog'

    ];

    /** The item that is being moved. */
    public target: any;
    /**
     * Flag passed by consumer that tells the dialog the user wants to copy items instead of move them.
     */
    public copy: boolean;
    /** Callback when a folder is created (most likely reloading background data). */
    public createFolderCallback: Function;
    /** Flag that will be true if the item being moved is a document. */
    public targetIsDocument: boolean;
    /** Flag that will be true if the item being moved is a folder. */
    public targetIsFolder: boolean;
    /** Number of items being used. This is used in the template to adjust the prompt on the submit button */
    public numItemsMoving: number;
    /** Parent of target moving. */
    public targetParent: DirectoryItem;
    /** Boolean that is true when the user is creating a document. */
    public creatingFolder:boolean;
    /** The new folder name that the user is creating. */
    public newFolderName:string = "";
    /** Regular expression for a proper folder name. */
    private regex = /^[^<>:"/\\|?*.]+([^<>:"/\\|?*](?=.))*[^<>:"/\\|?*.]*$/;
    /** Checked true a folder trying to be created already exists in the system. */
    private folderExists:boolean;

    private ItemType;

    private MetaRef;

    public get current() {
        if (this.folderNav.isInitializing) { return; }
        return this.folderNav.current;
    }
    public get isRoot() { 
        if (this.folderNav.isInitializing) { return; }
        return this.folderNav.isRoot;
    }

    public newFolderNameString = this.gettextCatalog.getString('New Folder Name');

    /**
     * Check if the item being moved can be moved into the current directory object
     * represented by @see folderNav.current
     */
    public get canMoveToCurrent(): boolean {
        if (this.folderNav.isInitializing) { return; }
        return this.canMoveTo(this.folderNav.current, false);
    }

    /**
     * Gets the collection of children for the current item in the
     * navigation directory. Using this property to access the children
     * puts a flag on each child that indicates if it can be navigated to.
     * It's done this way instead of binding in the template so as not to
     * keep invoking the logic for determining this even though it only
     * needs to be done once.
     */
    public get currentItemChildren(): any[] {
        if (this.folderNav.isInitializing) { return; }
        return this.folderNav.current.children.map((child: any) => {
            child.canMoveTo = this.canMoveTo(child);
            return child;
        });
    }


    constructor(
        private $timeout:ng.ITimeoutService,
        $mdDialog:ng.material.IAlertDialog, 
        $mdMedia:ng.material.IMedia,
        private pxProgressService:ProgressService,
        private charLimit: number,
        private folderNav: IDirectoryNavigationService, 
        private sbFolderService: IFolderService,
        private readonly sbToastService: ToastService,
        private readonly gettextCatalog: any
    ) {          
        super($mdDialog, template, gettextCatalog.getString('Move Item'), !$mdMedia('gt-sm'), true);  
    }

    public open(event:ng.IAngularEvent, target, copy:boolean = false, createFolderCallback:Function = angular.noop){
        this.target = target;
        this.copy = copy;
        this.createFolderCallback = createFolderCallback;

        this.creatingFolder = false;
        this.ItemType = ItemType;
        this.MetaRef = MetaRef;
        
        if (this.target instanceof Array) {
            this.targetIsDocument = this.target.some(pxItemTypeGuards.isShareBaseDocument);
            this.targetIsFolder = this.target.some(pxItemTypeGuards.isShareBaseFolder);
            this.numItemsMoving = this.target.length;
        } else {
            this.targetIsDocument = pxItemTypeGuards.isShareBaseDocument(this.target);
            this.targetIsFolder = pxItemTypeGuards.isShareBaseFolder(this.target);
            this.numItemsMoving = 1;
        }

        // initialize folder navigation each time a new dialog is created. This
        // will present using a stale directory structure.
        let initPromise = this.folderNav.init(this.target);
        this.pxProgressService.enqueue(initPromise);
        initPromise.then((navItems:DirectoryItem[]) => {
            this.targetParent = navItems[navItems.length - 1];
        });
        return super.open(event);
    }

    public moveItem(item, destination: DirectoryItem) {
        if (!this.hasCorrectPermissions(destination)) {
            let message = '';
            if (this.copy) {
                message = this.gettextCatalog.getString('You do not have the required permissions to copy documents to this destination');
            } else if (this.targetIsDocument && this.targetIsFolder) {
                message = this.gettextCatalog.getString('You do not have the required permissions to move items to this destination');
            } else if (this.targetIsDocument) {
                message = this.gettextCatalog.getString('You do not have the required permissions to move documents to this destination');
            } else {
                message = this.gettextCatalog.getString('You do not have the required permissions to move folders to this destination');
            }
            this.sbToastService.showSimple(message);
            return;
        }

        let result: any = { item, destination };
        if (this.target instanceof Array) {
            result.isCollection = true;
            result.collectionHasDocuments = this.targetIsDocument;
            result.collectionHasFolders = this.targetIsFolder;
        } else {
            result.isFolder = this.targetIsFolder;
            result.isDocument = this.targetIsDocument;
        }
        this.dismiss(result);
    }

    public hasCorrectPermissions(item: DirectoryItem) {
        // just ignore item if it does not have an ACL
        if (!item.acl) {
            return true;
        }
        let hasPerms = false;
        if (!pxItemTypeGuards.isShareBaseLibrary(item.ref) && this.targetIsDocument) {
            // Moving documents, user must have permission to create documents in
            // the target destination

            hasPerms = item.acl.hasPermission(PermissionType.ADDDOC);
        }
        if (this.targetIsFolder) {
            // Moving folders, user must have permission to create folders in
            // the target destination
            hasPerms = item.acl.hasPermission(PermissionType.ADDFOLD);
        }

        return hasPerms
    }

    public canMoveTo(item: DirectoryItem, forNavigation: boolean = true): boolean {
        // if forNavigation is true, the only time this should return false is when the
        // item and target represent the same folder or item is a folder in target if it is an array
        // Always allow users to navigate to meta items
        if (forNavigation) {
            // for navigation, always allow users to go to meta items
            if (item.itemType === ItemType.Meta) {
                return true;
            }
            
            if (!this.targetIsFolder) {
                // no need to check
                return true;
            }
            if (Array.isArray(this.target)) {
                // if item is in this.target, cannot move to it for navigation because
                // we cannot move a folder into itself or its children
                return this.target.filter(f => item.ref.FolderId === f.FolderId).length === 0;
            } else {
                return item.ref.FolderId !== this.target.FolderId;
            }
        }

        if (item.ref === MetaRef.Root || item.ref === MetaRef.SharedContent) {
            // nothing can be moved to root or shared content directory items
            return false;
        }

        // if (item.acl && !this.hasCorrectPermissions(item)) {
        //     return false;
        // }

        if (Array.isArray(this.target)) {
            // Moving a collection of items.
            // One of the following conditions must be met in order for all items in the collection
            // to be able to be moved to the directory represented by item:
            // 1. All objects in target array are folders and item is a library
            // 2. All objects in target array are folders and item is a folder that is NOT in the target array
            // 3. All objects in target array are documents and item is a folder
            // 4. The target array consists of both documents and folders and item is NOT a folder in the target array

            let allItemsAreFolders = this.target.every(pxItemTypeGuards.isShareBaseFolder);
            let allItemsAreDocuments = !allItemsAreFolders && this.target.every(pxItemTypeGuards.isShareBaseDocument);

            if (allItemsAreFolders) {
                return item.itemType === ItemType.Library || item.ref === MetaRef.MyFiles || this.target.filter(fold => fold.FolderId === item.ref.FolderId).length === 0;
            } else if (allItemsAreDocuments) {
                return item.itemType === ItemType.Folder;
            } else {
                let isItemInTarget = this.target.filter(pxItemTypeGuards.isShareBaseFolder).filter(fold => fold.FolderId === item.ref.FolderId).length > 0;
                return item.itemType !== ItemType.Library && !isItemInTarget;
            }

        } else {
            // Checking if can move a single item
            // 1. target is a folder and item is a library
            // 2. target is a folder and item is a folder, but NOT the same folder
            // 3. target is a document and item is a folder
            if (pxItemTypeGuards.isShareBaseFolder(this.target)) {
                return item.itemType === ItemType.Library || item.ref === MetaRef.MyFiles || item.ref.FolderId !== this.target.FolderId;
            } else {
                return item.itemType === ItemType.Folder;
            }
        }
    }

    public getEmptyMessage(dir:DirectoryItem){
        switch(dir.itemType){
            case 'Folder':
                return this.gettextCatalog.getString('No folders in {{directoryName}}', {
                    directoryName : dir.name
                });
            case 'Library':
                return this.gettextCatalog.getString('This library is empty');
        }        
    }

    // orderBy doesn't work internationally; use localeCompare
    private alphabetizeDirectories(directory) {
        return directory.sort((lhs, rhs) => {
            return lhs.name.localeCompare(rhs.name);
        })
    }

    private createFolder(){
        this.newFolderName = "";
        this.creatingFolder = true;
    }
    private cancelCreateFolder(){
        this.creatingFolder = false;
    }
    private confirmCreateFolder(form){
        let parentFolder = pxItemTypeGuards.isShareBaseFolder(this.folderNav.current.ref) ? this.folderNav.current.ref : undefined;
        this.newFolderName = this.newFolderName.trim();
        this.sbFolderService.createFolder(this.newFolderName, parentFolder)
            .then((res:ShareBaseFolder) => {
                let newChild = this.folderNav.addChild(res);
                let newFolderParent = angular.copy(this.folderNav.current);
                this.folderNav.goTo(newChild);
                this.creatingFolder = false;
                if (this.sameParent(newFolderParent.ref, this.targetParent.ref)){
                    this.createFolderCallback();
                }
            })
            .catch((err) => {
                var msg = '';
                switch (err.status) {
                    case 409:
                        msg = this.gettextCatalog.getString('Unable to create.  A folder with that name already exists.');
                        form.name.$error.folderExists = true;
                        form.name.$invalid = true;
                        this.$timeout(function () {
                            form.name.$error.folderExists = false;
                            form.name.$invalid = false;
                        },3000);
                        break;
                    default:
                        msg = this.gettextCatalog.getString('Unable to create folder');
                        break;
                }
                this.sbToastService.showSimple(msg);
            });
    }

    private sameParent(parent1, parent2){
        if (pxItemTypeGuards.isShareBaseFolder(parent1) && pxItemTypeGuards.isShareBaseFolder(parent2)){
            if (parent1.FolderId === parent2.FolderId) return true;
        } else if (parent1.ref === parent2.ref) {
            return true;
        }
        return false;
    }
}


// WEBPACK FOOTER //
// ./src/sharebase-app/components/moveItemDialog/moveItemDialog.ts